import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home as HomeIcon, MessageCircleHeart, CalendarHeart, Images, Settings as SettingsIcon, Heart } from "lucide-react";
import Home from "@/pages/Home";
import Daily from "@/pages/Daily";
import Dates from "@/pages/Dates";
import Memories from "@/pages/Memories";
import Settings from "@/pages/Settings";
import { partnerOf, meOf } from "@/lib/couple";
import { timeInZone } from "@/lib/timezones";

const TABS = [
  { key: "home", label: "Home", icon: HomeIcon },
  { key: "daily", label: "Daily", icon: MessageCircleHeart },
  { key: "dates", label: "Dates", icon: CalendarHeart },
  { key: "memories", label: "Wall", icon: Images },
  { key: "settings", label: "You", icon: SettingsIcon },
];

export default function Shell({ couple, slot, refreshCouple, onLogout }) {
  const [tab, setTab] = useState("home");
  const [, tick] = useState(0);
  const partner = partnerOf(couple, slot);
  const me = meOf(couple, slot);

  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const partnerTime = partner?.timezone ? timeInZone(partner.timezone) : null;

  return (
    <div className="max-w-md mx-auto min-h-screen relative pb-24">
      {/* header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/85 border-b border-border/70 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center shadow">
            <Heart className="h-4 w-4 text-primary-foreground fill-primary-foreground" />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-lg font-semibold">
              {couple.couple_title || "Us"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              {couple.connected ? "Connected" : "Waiting for partner"}
            </div>
          </div>
        </div>
        {partner?.name && partnerTime && (
          <div className="text-right" data-testid="header-partner-time">
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{partner.name}'s time</div>
            <div className="font-mono text-sm font-semibold text-primary">{partnerTime.time}</div>
          </div>
        )}
      </header>

      <main className="px-5 pt-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
          >
            {tab === "home" && <Home couple={couple} slot={slot} me={me} partner={partner} setTab={setTab} />}
            {tab === "daily" && <Daily couple={couple} slot={slot} me={me} partner={partner} />}
            {tab === "dates" && <Dates couple={couple} />}
            {tab === "memories" && <Memories couple={couple} slot={slot} me={me} partner={partner} />}
            {tab === "settings" && <Settings couple={couple} slot={slot} refreshCouple={refreshCouple} onLogout={onLogout} />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 max-w-md mx-auto">
        <div className="m-3 rounded-2xl bg-card/90 backdrop-blur-xl border border-border shadow-lg px-2 py-2 flex justify-around items-center">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`nav-${t.key}`}
                onClick={() => setTab(t.key)}
                className="relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors"
              >
                {active && (
                  <motion.div layoutId="nav-pill" className="absolute inset-0 bg-accent rounded-xl" transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                )}
                <Icon className={`relative h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                <span className={`relative text-[10px] font-medium ${active ? "text-primary" : "text-muted-foreground"}`}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
